import React, { useState, useEffect } from 'react';
import slide1 from '../../assets/images/slide1.png';
import slide2 from '../../assets/images/slide2.png';
import slide3 from '../../assets/images/slide3.png';
import slide4 from '../../assets/images/slide4.png';
import slide5 from '../../assets/images/slide5.png';
import './AuthIntroSection.css';
import { useTranslation } from 'react-i18next';

/**
 * AuthIntroSection Component
 * 
 * Left content section with auto-rotating carousel displaying branding, 
 * heading, description, and illustrations for authentication pages
 * 
 * Features:
 * - 4 slides with auto-rotation
 * - Smooth transitions
 * - Interactive dot indicators
 * - Continuous loop
 * 
 * @component
 */

const AuthIntroSection = () => {
  const { t } = useTranslation('auth');
  
  // Current active slide index
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Carousel slides data with translation keys
  const SLIDES = [
    {
      id: 1,
      heading: (
        <>
          <span style={{ color: '#FCBA05' }}>{t('realTime')}</span>, {t('retailDigitalMarketplace')}
        </>
      ),
      description: t('retailMarketplaceDescription'),
      image: slide2,
      alt: t('retailMarketplaceIllustration'),
    },
    {
      id: 2,
      heading: (
        <>
          <span style={{ color: '#FCBA05' }}>{t('moreAffordable')}</span> {t('thanTheRestSmarterForYou')}
        </>
      ),
      description: t('affordableSolutionDescription'),
      image: slide3,
      alt: t('affordableSolutionIllustration'),
    },
    {
      id: 3,
      heading: (
        <>
          <span style={{ color: '#FCBA05' }}>{t('multiPlatform')}</span>, {t('onMobileAndWebDownloadNow')}
        </>
      ),
      description: t('multiPlatformDescription'),
      image: slide4,
      alt: t('multiPlatformIllustration'),
    },
    {
      id: 4,
      heading: (
        <>
          <span style={{ color: '#FCBA05' }}>{t('serveMoreSpendLess')}</span> {t('withChatOrdering')}
        </>
      ),
      description: t('chatOrderingDescription'),
      image: slide5,
      alt: t('chatOrderingIllustration'),
    },
    {
      id: 5,
      heading: (
        <>
          {t('your')} <span style={{ color: '#FCBA05' }}>{t('digitalGrowth')}</span> {t('commandCenter')}
        </>
      ),
      description: t('digitalGrowthDescription'),
      image: slide1,
      alt: t('digitalGrowthIllustration'),
    },
  ];
  
  // Auto-rotate carousel every 8-10 seconds (tripled from original)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % SLIDES.length);
    }, 9000); // 9 seconds per slide (3x the original 3 seconds)

    return () => clearInterval(interval);
  }, []);

  // Handle dot click
  const handleDotClick = (index) => {
    setCurrentSlide(index);
  };

  // Get current slide data
  const currentSlideData = SLIDES[currentSlide];

  return (
    <div className="auth-intro">
      {/* Content wrapper */}
      <div className="auth-intro__content">
        {/* Carousel slide content with fade transition */}
        <div className="auth-intro__slide" key={currentSlideData.id}>
          {/* Heading */}
          <h1 className="auth-intro__heading">
            {currentSlideData.heading}
          </h1>

          {/* Description */}
          <p className="auth-intro__description">
            {currentSlideData.description}
          </p>

          {/* Illustration */}
          <div className="auth-intro__illustration">
            <img 
              src={currentSlideData.image} 
              alt={currentSlideData.alt}
              className="auth-intro__image"
            />
          </div>
        </div>

        {/* Slider dots indicator (interactive) */}
        <div className="auth-intro__slider-dots">
          {SLIDES.map((slide, index) => (
            <button
              key={slide.id}
              className={`auth-intro__dot ${index === currentSlide ? 'auth-intro__dot--active' : ''}`}
              onClick={() => handleDotClick(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AuthIntroSection;
